import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticationService } from '../../services/authentication.service';
import { Sidenav, Nav, Image, Tooltip, Whisper } from 'rsuite';
import { createSafeInternalUrl } from '../../utils/helpers';
import DarkModeToggle from '../dark-mode-toggle';
import {
  BESTSTART_LOGO_WHITE_SMALL,
  LOGO,
} from '../../utils/constants/images';
import { version } from '../../../package.json';
import { IoHome } from 'react-icons/io5';
import { IoSettingsOutline } from 'react-icons/io5';
import { IoMdLogOut } from 'react-icons/io';
import { useTheme } from '../../contexts/ThemeContext';
import { Hamburger } from '..';
import ImpersonationPicker from '../impersonation-picker';
import { AppSwitcher } from '@best-start/beststart.react.app-switcher';
import './navigation.css';

const APP_SWITCHER_CONFIG_URL = "https://cdn.best-start.org/app-switcher/app-switcher.config.json";

function Navigation({ setMargin }) {
  const MIN_MARGIN = 50;
  const MAX_MARGIN = 200;
  const navRef = useRef(null);
  const scrollableNavRef = useRef(null);
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [activeKey, setActiveKey] = useState('1');
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);
  const navigate = useNavigate();
  const authService = useAuthenticationService();
  const { darkMode, setDarkMode } = useTheme();

  const handleSignOut = async () => {
    await authService.logout();
  };

  const paths = useMemo(
    () => [
      {
        icon: <IoHome className='rs-icon' />,
        path: '/',
        key: '1',
        name: 'Home',
        visible: true,
      },
      {
        icon: <IoMdLogOut className='rs-icon' />,
        path: '/signout',
        key: '9',
        name: 'Log Out',
        testId: 'logout-button',
        footer: true,
        visible: true,
      },
    ],
    [],
  );

  function matchPath(path, activePath) {
    const cleanPath = path.split('?')[0];
    const cleanActivePath = activePath.split('?')[0];
    if (cleanPath === cleanActivePath) return true;
    if (cleanPath !== '/' && cleanActivePath.startsWith(cleanPath + '/'))
      return true;
    return false;
  }

  const navigateTopath = useCallback(
    (path) => {
      if (location.pathname === path) {
        setExpanded(false);
        setMargin && setMargin(MIN_MARGIN);
        return;
      }
      path === '/signout' ? handleSignOut() : navigate(path);
      setExpanded(false);
      setMargin && setMargin(MIN_MARGIN);
    },
    [location.pathname, navigate, setMargin, handleSignOut],
  );

  const handleClickOutside = useCallback(
    (event) => {
      if (
        event.target.closest('.app-switcher-dropdown') ||
        event.target.closest('[class*="AppSwitcher"]')
      ) {
        return;
      }
      if (navRef.current && !navRef.current.contains(event.target)) {
        setExpanded(false);
        setMargin && setMargin(MIN_MARGIN);
      }
    },
    [setMargin],
  );

  function onToggle(value) {
    setDarkMode(value);
  }

  function sendClick(event) {
    setExpanded(event.target.checked);
    setMargin && setMargin(event.target.checked ? MAX_MARGIN : MIN_MARGIN);
  }

  useEffect(() => {
    const activeItem = paths.find((item) =>
      matchPath(item.path, location.pathname),
    );
    setActiveKey(activeItem ? activeItem.key : '1');
    if (activeItem) {
      document.title = `${activeItem.name} - BestStart KPI Scorecard`;
    } else {
      document.title = 'BestStart KPI Scorecard';
    }
  }, [location.pathname, paths]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleSwipe = useCallback(
    (event) => {
      if (
        event.target &&
        event.target.closest &&
        (event.target.closest('.app-switcher-container') ||
          event.target.closest('[class*="AppSwitcher"]') ||
          event.target.closest('[class*="rs-dropdown"]') ||
          event.target.closest('[class*="rs-popover"]'))
      ) {
        return;
      }

      const touch = event.touches[0];
      const swipeThreshold = 100;

      if (event.type === 'touchstart') {
        navRef.current.startX = touch.clientX;
      } else if (event.type === 'touchmove') {
        const deltaX = touch.clientX - navRef.current.startX;
        if (deltaX > swipeThreshold) {
          setExpanded(true);
          setMargin && setMargin(MAX_MARGIN);
        } else if (deltaX < -swipeThreshold) {
          setExpanded(false);
          setMargin && setMargin(MIN_MARGIN);
        }
      }
    },
    [setMargin],
  );

  useEffect(() => {
    setMargin && setMargin(expanded ? MAX_MARGIN : MIN_MARGIN);
    document.addEventListener('touchstart', handleSwipe);
    document.addEventListener('touchmove', handleSwipe);
    return () => {
      document.removeEventListener('touchstart', handleSwipe);
      document.removeEventListener('touchmove', handleSwipe);
    };
  }, [expanded, setMargin, handleSwipe]);

  const checkScrollIndicators = useCallback(() => {
    if (!scrollableNavRef.current) return;
    const element = scrollableNavRef.current;
    const { scrollTop, scrollHeight, clientHeight } = element;
    setShowTopIndicator(scrollTop > 10);
    setShowBottomIndicator(scrollTop < scrollHeight - clientHeight - 10);
  }, []);

  const handleNavScroll = useCallback(
    (event) => {
      event.preventDefault();
      const delta = event.deltaY;
      if (scrollableNavRef.current) {
        scrollableNavRef.current.scrollTop += delta;
        checkScrollIndicators();
      }
    },
    [checkScrollIndicators],
  );

  const handleMouseDown = useCallback((event) => {
    if (!scrollableNavRef.current) return;
    if (event.target.closest('.app-switcher-container')) return;
    setIsDragging(true);
    setDragStartY(event.clientY);
    setDragStartScrollTop(scrollableNavRef.current.scrollTop);
    event.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !scrollableNavRef.current) return;
      event.preventDefault();
      const deltaY = event.clientY - dragStartY;
      scrollableNavRef.current.scrollTop = Math.max(
        0,
        dragStartScrollTop - deltaY,
      );
      checkScrollIndicators();
    },
    [isDragging, dragStartY, dragStartScrollTop, checkScrollIndicators],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    checkScrollIndicators();
    if (scrollableNavRef.current) {
      const element = scrollableNavRef.current;
      element.addEventListener('scroll', checkScrollIndicators);
      element.addEventListener('wheel', handleNavScroll, { passive: false });
      const resizeObserver = new ResizeObserver(checkScrollIndicators);
      resizeObserver.observe(element);
      return () => {
        element.removeEventListener('scroll', checkScrollIndicators);
        element.removeEventListener('wheel', handleNavScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScrollIndicators, handleNavScroll, expanded]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      style={{
        position: 'fixed',
        width: expanded ? MAX_MARGIN : 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        height: '100dvh',
        zIndex: 9000,
      }}
      ref={navRef}>
      <Sidenav
        expanded={expanded}
        className='side-bar'
        data-testid='navbar'
        data-expanded={expanded}>
        <Sidenav.Body
          style={{
            paddingTop: 10,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
          <div className={`hamburger-wrapper ${expanded ? 'expanded' : ''}`}>
            <Hamburger checked={expanded} onClick={sendClick} />
          </div>

          <Whisper
            placement='right'
            trigger='hover'
            speaker={<Tooltip>Version: {version}</Tooltip>}>
            <div className='logo-container' aria-label={`Version: ${version}`}>
              <Image
                src={BESTSTART_LOGO_WHITE_SMALL}
                alt='BestStart Logo'
                width='100%'
                height={45}
                style={{ display: expanded ? '' : 'none' }}
              />
              <Image
                src={LOGO}
                className='logo-small'
                alt='BestStart Logo'
                width={45}
                height={45}
                style={{ display: !expanded ? '' : 'none', maxWidth: 45 }}
              />
            </div>
          </Whisper>

          <div className='nav-scroll-container'>
            {showTopIndicator && (
              <div
                className='scroll-indicator scroll-indicator-top'
                onClick={() => {
                  if (scrollableNavRef.current)
                    scrollableNavRef.current.scrollTop = Math.max(
                      0,
                      scrollableNavRef.current.scrollTop - 100,
                    );
                  checkScrollIndicators();
                }}
                style={{ cursor: 'pointer' }}>
                <div className='scroll-indicator-gradient'></div>
              </div>
            )}

            <div
              className='scrollable-nav'
              ref={scrollableNavRef}
              onMouseDown={handleMouseDown}
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
              }}>
              <Nav activeKey={activeKey} onSelect={setActiveKey}>
                {paths &&
                  paths
                    .filter((path) => !path.footer)
                    .map((item) => (
                      <Nav.Item
                        data-testid={`${item.testId || item.key}-web`}
                        key={item.key}
                        onClick={navigateTopath.bind(null, item.path)}
                        onAuxClick={(e) => {
                          if (e.button === 1 && item.path !== '/signout') {
                            if (
                              item.path &&
                              typeof item.path === 'string' &&
                              item.path.startsWith('/') &&
                              !item.path.startsWith('//')
                            ) {
                              const safeUrl = createSafeInternalUrl(
                                window.location.origin,
                                item.path,
                              );
                              if (safeUrl) window.open(safeUrl, '_blank');
                            }
                          }
                        }}
                        eventKey={item.key}
                        icon={item.icon}
                        disabled={!item.visible}
                        style={{ userSelect: 'none' }}>
                        {item.name}
                      </Nav.Item>
                    ))}
              </Nav>
            </div>

            {showBottomIndicator && (
              <div
                className='scroll-indicator scroll-indicator-bottom'
                onClick={() => {
                  if (scrollableNavRef.current)
                    scrollableNavRef.current.scrollTop += 100;
                  checkScrollIndicators();
                }}
                style={{ cursor: 'pointer' }}>
                <div className='scroll-indicator-gradient'></div>
              </div>
            )}
          </div>
        </Sidenav.Body>

        <div className='nav-footer'>
          <ImpersonationPicker expanded={expanded} />
          {expanded && <div className='nav-footer-label'>Settings</div>}
          <div className='theme-row'>
            <span className='theme-row__text'>
              <span className='theme-row__title'>Dark mode</span>
            </span>
            <DarkModeToggle onToggle={onToggle} defaultChecked={darkMode} />
          </div>
          <Nav
            activeKey={activeKey}
            onSelect={setActiveKey}
            className='bottom-nav'>
            {paths &&
              paths
                .filter((path) => path.footer)
                .map((item) => (
                  <Nav.Item
                    data-testid={`${item.testId || item.key}-footer`}
                    key={item.key}
                    onClick={navigateTopath.bind(null, item.path)}
                    eventKey={item.key}
                    icon={item.icon}>
                    {item.name}
                  </Nav.Item>
                ))}
          </Nav>
        </div>
      </Sidenav>

      <div
        className='app-switcher-container'
        onClick={(e) => e.stopPropagation()}>
        <AppSwitcher
          currentAppId='scorecard'
          authenticationService={authService}
          expanded={expanded}
          manifestUrl={APP_SWITCHER_CONFIG_URL}
        />
      </div>
    </div>
  );
}

export default React.memo(Navigation);
